import { loadVoicePreview } from './voice-preview';
import { isServerMode } from '../../platform/server-mode';
import { accountLocale, uiText } from '../../i18n';
import { reduced } from '../../ui/motion';
import { FILTER_SUB, openFilterSheet } from '../../ui/filter-sheet';
import './voice-skeleton.css';
import './voice-empty.css';
import './voice-collection.css';
import './voice-row.css';
/* аватарки «Озвучки» и панели собираются из пикселей, когда приходят (модуль сам следит за разметкой) */
import './pixel-reveal';
/* Выбор голоса — тот же виджет, что SteosUI.voicePicker и собранный из него BotPicker.
   Разметка строки в строку та же: быстрый поиск каркаса перехватывает innerHTML корня и собирает выдачу на месте.
   Колонку («Озвучка», правая панель, «Другой голос») рисует React и отдаёт сюда разметку через commit. */

export interface VoiceItem {
  id: string | number;
  name: string;
  tags?: string[];
  sex?: string | null;
  c?: string[];
  top?: boolean | number;
  trend?: number;
  isNew?: boolean | number;
  orb?: string;
  mine?: boolean;
  avatar_url?: string | null;
  preview_url?: string | null;
}

interface CollItem {
  id: string;
  name: string;
  desc: string;
  ic?: string;
  img?: string;
  f?: string;
  count?: number;
  /** Персонажей в подборке: вторая цифра карточки */
  charCount?: number;
  /** Кружки карточки от сервера (preview_voices); без них — голоса подборки из списка */
  faces?: string[];
  match?: (v: VoiceItem) => boolean;
  /** Порядок голосов открытой подборки без сервера (у «Избранного» — новые первыми) */
  ord?: (vs: VoiceItem[]) => VoiceItem[];
  /** Свечение карточки (--cg), если обложка не годится для него (у «Избранного» на обложке сердце) */
  cg?: string;
}

interface FindGroup {
  title: string;
  items: VoiceItem[];
  row: (item: VoiceItem) => string;
  voices?: Array<string | number>;
}

interface Query {
  title?: string;
  vs: VoiceItem[];
  cs?: CollItem[];
  coll?: CollItem;
  onlyColls?: boolean;
  xt?: string;
  xs?: FindGroup | null;
  /** Страница «Рекомендуем»: подборки, персонажи и голоса группами, как выдача поиска */
  reco?: boolean;
  /** Открытая подборка с персонажами: группы «Персонажи» и «Голоса» одним списком, персонажи сверху */
  charsFirst?: boolean;
}

/** Состав страницы «Рекомендуем» (docs/APP-SPEC-1-tts.md §3.2): 3 подборки, 10 персонажей, 10 голосов */
const RECO_COLLS = 3;
const RECO_VOICES = 10;

export interface VoiceListState {
  q: string;
  tab: string;
  coll: string | null;
  sort: string;
  shown: number;
  top: number;
  playing: string | number | null;
  value: unknown;
  /** Что показывать внутри подборки с персонажами и голосами: всё, только персонажей или только голоса (вторая строка вкладок) */
  part?: 'all' | 'chars' | 'voices';
  /** Поле поиска раскрыто поверх вкладок (лупа в строке вкладок; с запросом — раскрыто всегда) */
  qo?: boolean;
  /** Отбор листа ⇅ «Голоса»: 's2s' — только голоса с переозвучкой записи (is_s2s; сервер отбирает сам, can_s2s) */
  only?: 's2s' | '';
  /** Плашка над списком (шаблонная .note): вход из бота за голосом для переозвучки (go_tts_s2s), после выбора — «Голос выбран» */
  note?: { text: string; kind?: 'mute' | 'ok' } | null;
}

export interface VoiceListOpts {
  el: HTMLElement;
  voices: VoiceItem[] | (() => VoiceItem[]);
  remote?: (state: Pick<VoiceListState, 'tab' | 'q' | 'sort' | 'coll' | 'only'>, cursor?: string) => Promise<{
    items: VoiceItem[]; nextCursor: string | null; total: number; catalogTotal: number;
    /** «Рекомендуем» целиком: подборки и id персонажей приходят вместе с первой страницей голосов */
    collections?: CollItem[]; characterIds?: Array<string | number>;
  }>;
  remoteCollections?: () => Promise<CollItem[]>;
  /** Первые n подборок одной короткой страницей — для «Рекомендуем», чтобы не ждать весь набор */
  remoteCollectionsTop?: (n: number) => Promise<CollItem[]>;
  colls?: CollItem[];
  total?: number;
  lang?: string;
  value?: unknown;
  state?: Partial<VoiceListState>;
  tabs?: [string, string][];
  lists?: Record<string, (voices: VoiceItem[]) => VoiceItem[]>;
  rows?: Record<string, (item: VoiceItem) => string>;
  empty?: Record<string, string>;
  find?: (q: string) => FindGroup;
  reco?: (voices: VoiceItem[]) => VoiceItem[];
  /** Персонажи страницы «Рекомендуем» в порядке рейтинга (до 10) */
  recoChars?: (ids?: Array<string | number>) => FindGroup | null;
  /** Персонажи открытой подборки (их ставит администратор рядом с голосами): раздел «Персонажи» над голосами */
  collChars?: (collId: string) => FindGroup | null;
  /** Подборка «Избранное» (bridge/favorites.ts): первой в «Подборках» и на «Рекомендуем»; null — избранное пусто, карточки нет */
  favColl?: () => CollItem | null;
  /** Тег у имени: строка или { t, c, b } — текст, класс вида и бейдж версии (eng — модель синтеза «Voice ¹», без значка клона) */
  tag?: (voice: VoiceItem) => string | { t: string; c: string; b?: string };
  t?: (k: string, v?: Record<string, unknown>) => string;
  /** Голос умеет переозвучку записи — отбор «Переозвучка записи» без сервера (прототип) */
  canS2s?: (voice: VoiceItem) => boolean;
  /** Список уже отобран по переозвучке (выбор голоса экранов «только с переозвучкой») — группы «Голоса» в листе ⇅ нет */
  onlyFixed?: boolean;
  onPick?: (id: string | number, voice: VoiceItem) => void;
  onClose?: () => void;
}

/** Куда React кладёт разметку. after — бегунок вкладок и прокрутка, уже после вставки узлов */
export type VoiceCommit = (html: string, after: () => void) => void;

const esc = (s: unknown) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

const bit = (v: unknown) => (v ? 1 : 0);

/* Загрузка списка — скелетон, без надписей (как у шаблона): строки голосов (SK.tts шаблона) и карточки подборок */
const SK_W = ['34%', '26%', '30%', '40%', '28%', '36%', '32%', '38%', '30%'];
const SK_S = ['52%', '44%', '48%', '58%', '40%', '54%', '46%', '50%', '42%'];
const skB = (w: string, h: number, o?: boolean) => `<i class="b${o ? ' o' : ''}" style="width:${w};height:${h}px"></i>`;
/* порог появления отсчитывается от начала загрузки (at — задержка анимации, мс): перерисовки во время загрузки не начинают проявление заново */
const skAt = (at: number) => ` style="animation-delay:${Math.round(at)}ms"`;
const skRows = (n: number, at: number, attrs = '') =>
  `<div class="lsk"${skAt(at)}${attrs} aria-hidden="true">${Array.from({ length: n }, (_, i) =>
    `<div class="lsk-r" style="--d:${60 * i}ms">${skB('40px', 40, true)}<div class="lsk-c">${skB(SK_W[i % 9], 12)}${skB(SK_S[i % 9], 10)}</div></div>`).join('')}</div>`;
/* заглушка карточки подборки — по форме .ccd: слева название, описание в две строки, ряд лиц и счётчики, справа круглая обложка;
   карточки стопкой, как настоящие (voice-skeleton.css) */
const skCards = (n: number, at: number) =>
  `<div class="lsk lskc"${skAt(at)} aria-hidden="true">${Array.from({ length: n }, (_, i) =>
    `<div class="lsk-k" style="--d:${60 * i}ms"><div class="lsk-km">${skB(['52%', '44%', '58%', '48%'][i % 4], 14)}${skB('92%', 11)}${skB(['64%', '72%', '56%', '68%'][i % 4], 11)}<span class="lsk-kf">${skB('26px', 26, true).repeat(4)}</span>${skB('72%', 10)}</div>${skB('68px', 68, true)}</div>`).join('')}</div>`;

type RecoExtra = { colls: CollItem[]; ids: Array<string | number> };
type CachedPage = { at: number; items: VoiceItem[]; cursor: string | null; total: number; catalogTotal: number; reco: RecoExtra | null };
/* Загруженные выдачи и подборки живут дольше одного списка: каркас снимает список при уходе со вкладки и ставит новый при возврате,
   и без общей памяти возврат начинался со скелетона, новой загрузки и повторного входа строк (интерфейс моргал), а открытая подборка
   теряла свою плашку — новый список ещё не знал подборок. Ключ — функция каталога: у «Озвучки» и панели она общая (BotVoiceCatalog),
   у переозвучки своя обёртка на каждый показ — её выдача с общей не смешивается */
let PAGES = new WeakMap<object, Map<string, CachedPage>>();
let COLLS = new WeakMap<object, CollItem[]>();
let HEADS = new WeakMap<object, CollItem[]>();
/* Память — на языке профиля: язык меняют в «Настройках», когда списка «Озвучки» на экране нет и его onLocale не слышит смену,
   и новый список брал из памяти подборки и выдачи на прежнем языке */
document.addEventListener('steos:locale', () => {
  PAGES = new WeakMap();
  COLLS = new WeakMap();
  HEADS = new WeakMap();
});

/** Список голосов в уже стоящей колонке. state — тот же объект, что читает каркас */
export function createVoiceList(o: VoiceListOpts, commit: VoiceCommit) {
  const { pop, segInd, syncGutters, icons: I } = window.SteosUI;
  const el = o.el;
  /* словарь профиля в момент показа: шаблон переводится целиком, подстановки {n} — после перевода и как есть */
  const t =
    o.t ||
    ((k: string, v?: Record<string, unknown>) => {
      const s = uiText(String(k));
      return v ? s.replace(/\{(\w+)\}/g, (m, n: string) => (v[n] == null ? m : String(v[n]))) : s;
    });
  /** Пустая выдача — карточка на всю высоту, как пустой поиск «Студии» (voice-empty.css, .vmz): веер призрачных карточек голоса и линза с лупой;
      по запросу — с текстом запроса и «Очистить поиск» (data-vclear — тот же сброс, что крестик в поле) */
  const zero = (q: string) =>
    `<div class="vmz"><div class="zs" aria-hidden="true"><span class="zf"></span><span class="zt"><i></i><i></i><i></i></span><span class="zo">${I.search}</span></div><b>${esc(t('Ничего не нашлось'))}</b><p>${esc(q ? t('По запросу «{q}» голосов нет. Попробуйте другое слово или загляните в подборки', { q }) : t('Здесь пока нет голосов. Загляните в другие вкладки или подборки'))}</p>${q ? `<button class="btn sm" type="button" data-vclear>${I.x}${esc(t('Очистить поиск'))}</button>` : ''}</div>`;
  const O = (a: string, b: string) => `conic-gradient(from 210deg at 50% 50%,${a},${b},${a})`;
  const SYS: CollItem[] = [
    {
      id: 'sys_trend',
      name: 'Тренды недели',
      desc: 'Голоса, которые быстрее всего набирают заказы',
      ic: O('#fde68a', '#b45309'),
      match: v => !!v.trend,
    },
    { id: 'sys_m', name: 'Мужские', desc: 'Все мужские голоса библиотеки', ic: O('#93c5fd', '#1e3a8a'), match: v => v.sex === 'm' },
    { id: 'sys_f', name: 'Женские', desc: 'Все женские голоса библиотеки', ic: O('#f9a8d4', '#9d174d'), match: v => v.sex === 'f' },
    { id: 'sys_k', name: 'Детские', desc: 'Детские и мультяшные голоса', ic: O('#bbf7d0', '#166534'), match: v => v.sex === 'k' },
  ];
  const TABS = o.tabs || [
    ['reco', 'Рекомендуем'],
    ['colls', 'Подборки'],
    ['new', 'Новые'],
  ];
  const SORTS: [string, string][] = [
    ['pop', 'Популярные'],
    ['new', 'Новые'],
    ['name', 'От А до Я'],
    ['name_desc', 'От Я до А'],
  ];
  const st: VoiceListState = Object.assign(
    { q: '', tab: 'reco', coll: null, sort: 'pop', shown: 14, top: 0, playing: null, value: o.value },
    o.state || {},
  );
  let pt: ReturnType<typeof setInterval> | null = null;
  let preview: HTMLAudioElement | null = null;
  let previewError = '';
  let previewLoading: string | null = null;
  let previewRequest: AbortController | null = null;
  let releasePreview: (() => void) | null = null;
  const same = (a: unknown, b: unknown) => String(a) === String(b);
  const voices = () => {
    const vs = typeof o.voices === 'function' ? o.voices() : o.voices;
    return st.only === 's2s' && o.canS2s ? vs.filter(o.canS2s) : vs;
  };
  let remoteItems: VoiceItem[] = [];
  let remoteCursor: string | null = null;
  let remoteTotal = 0;
  let remoteCatalogTotal = o.total || 0;
  let remoteLoading = false;
  let remoteError = false;
  /* подборки и персонажи «Рекомендуем» от сервера (первая страница reco); null — сервер их не прислал, тогда подборки грузятся отдельно */
  let remoteReco: RecoExtra | null = null;
  let remoteRevision = 0;
  let remoteTimer: ReturnType<typeof setTimeout> | null = null;
  let remoteColls: CollItem[] = (o.remoteCollections && COLLS.get(o.remoteCollections)) || [];
  const keepColls = (items: CollItem[]) => {
    remoteColls = items;
    if (o.remoteCollections) COLLS.set(o.remoteCollections, items);
  };
  /* первые подборки «Рекомендуем»: показываются, не дожидаясь остальных страниц */
  let recoHead: CollItem[] = (o.remoteCollectionsTop && HEADS.get(o.remoteCollectionsTop)) || [];
  let collectionsLoading = false;
  /* Загруженные выдачи вкладок и подборок (без поиска): возврат на вкладку — сразу с прежними строками, без скелетона и нового запроса,
     и прокрутка, которую возвращает каркас, ложится на уже загруженный список. Живут CACHE_MS; refresh() сбрасывает выдачу на экране, смена языка — все */
  const CACHE_MS = 120_000;
  const cache = (o.remote && PAGES.get(o.remote)) || new Map<string, CachedPage>();
  if (o.remote) PAGES.set(o.remote, cache);
  /** Какую выдачу должен показывать список. Каркас меняет состояние и мимо нажатий (подборка при возврате на «Подборки», pickerAfter) —
      render() по расхождению с reqKey сам запрашивает выдачу, иначе показал бы «Ничего не нашлось» вместо голосов подборки */
  const keyOf = () => [st.tab, st.q.trim(), st.sort, st.coll ?? '', st.only ?? ''].join('\u0001');
  let reqKey = '';
  /* «Подборки» и «Персонажи» — свои списки (подборки и каталог персонажей), голоса с сервера идут у остальных вкладок, в подборке и в поиске */
  /* вкладки со своей строкой (o.rows: «Персонажи», «Актёры») — локальные списки каталога персонажей, не страницы голосов сервера */
  const remoteActive = () => !!o.remote && (!!st.q.trim() || (st.tab !== 'colls' && !o.rows?.[st.tab]) || !!st.coll);
  /** «Избранное» встаёт первой к любому списку подборок, пока в нём что-то есть */
  const withFav = (cs: CollItem[]) => {
    const f = o.favColl?.();
    return f ? [f, ...cs.filter(c => c.id !== f.id)] : cs;
  };
  const colls = () => withFav(o.remoteCollections ? remoteColls : isServerMode() ? o.colls || [] : [...SYS, ...(o.colls || [])]);
  const inColl = (v: VoiceItem, c: CollItem) => (c.match ? c.match(v) : (v.c || []).includes(c.id));
  /* подборка, открытая из «Рекомендуем», была только в подборках той страницы (полный список вкладки «Подборки» не грузился, а подборки
     «Рекомендуем» сбрасывает новый запрос): openColl запоминает её карточку. Без неё не было островка с названием и «Закрыть подборку»,
     а вместе с ним — «Назад» Telegram */
  let openedColl: CollItem | null = null;
  const collOf = (id: string | null) =>
    colls().find(c => c.id === id) ?? remoteReco?.colls.find(c => c.id === id) ?? recoHead.find(c => c.id === id) ?? (openedColl?.id === id ? openedColl : undefined);
  const cname = (c: CollItem) => (c.match ? t(c.name) : c.name);
  const cdesc = (c: CollItem) => (c.match ? t(c.desc) : c.desc);
  const cimg = (c: CollItem) =>
    c.img
      ? `<img src="${esc(c.img)}" alt="" style="filter:${c.f || 'none'}" draggable="false">`
      : `<span style="display:block;width:100%;height:100%;background:${c.ic}"></span>`;
  const lang = () => o.lang && o.lang !== 'ru' ? o.lang : accountLocale();
  const num = (n: number) => Number(n).toLocaleString(lang() === 'ru' ? 'ru-RU' : lang());
  const reset = () => {
    st.shown = 14;
    st.top = 0;
  };
  /** Поиск без регистра, «ё» = «е» (§4.3 docs/03-api.md) */
  const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е');
  const isReco = () => st.tab === 'reco' && !st.q.trim() && !st.coll;
  /** Подборки страницы «Рекомендуем»: у сервера порядок задан (закреплённые, затем по синтезам); в демо — по числу рекомендованных голосов */
  const recoColls = () => {
    const cs = colls();
    if (remoteReco?.colls.length) return withFav(remoteReco.colls).slice(0, RECO_COLLS);
    if (o.remoteCollections) return (remoteColls.length ? cs : withFav(recoHead)).slice(0, RECO_COLLS);
    const score = (c: CollItem) => (c.id === 'fav' ? Infinity : voices().filter(v => v.top && !v.mine && inColl(v, c)).length);
    return [...cs].sort((p, n) => score(n) - score(p)).slice(0, RECO_COLLS);
  };
  const recoQuery = (vs: VoiceItem[]): Query => ({
    reco: true,
    title: t('Голоса'),
    vs: vs.slice(0, RECO_VOICES),
    cs: recoColls(),
    xs: o.recoChars ? o.recoChars(remoteReco?.ids) : null,
  });
  function query(): Query {
    if (remoteActive()) {
      const q = norm(st.q.trim());
      if (isReco()) return recoQuery(remoteItems);
      if (!q && st.coll) {
        const xs = o.collChars ? o.collChars(st.coll) : null;
        return { vs: remoteItems, title: t('Голоса'), xs, charsFirst: !!xs };
      }
      if (!q) return { vs: remoteItems, title: t('Голоса'), xt: st.tab === 'mine' ? 'mine' : undefined };
      /* голоса ищет сервер; группы «Подборки» (по названию и описанию) и «Персонажи» — по уже загруженным подборкам и каталогу персонажей,
         как у шаблона: подборки над голосами, персонажи под ними */
      return {
        vs: remoteItems,
        title: t('Голоса'),
        xs: o.find ? o.find(q) : null,
        cs: colls().filter(c => norm(cname(c)).includes(q) || norm(cdesc(c)).includes(q)),
      };
    }
    const V = voices();
    const q = st.q.trim().toLowerCase();
    if (q) {
      const xs = o.find ? o.find(q) : null;
      const xv = xs && xs.voices ? xs.voices.map(String) : [];
      return {
        xs,
        title: t('Голоса'),
        vs: V.filter(
          x => x.name.toLowerCase().includes(q) || (x.tags || []).join(' ').toLowerCase().includes(q) || xv.includes(String(x.id)),
        ),
        cs: colls().filter(c => cname(c).toLowerCase().includes(q) || cdesc(c).toLowerCase().includes(q)),
      };
    }
    if (st.coll) {
      const c = collOf(st.coll);
      if (!c) return { vs: [] };
      const vs = V.filter(x => inColl(x, c));
      /* у «Избранного» и без сервера — группы «Персонажи и актёры · Голоса», как у подборки с персонажами сервера */
      const xs = c.id === 'fav' && o.collChars ? o.collChars(c.id) : null;
      return { coll: c, vs: c.id === 'sys_trend' ? vs.sort((p, n) => (p.trend || 0) - (n.trend || 0)) : c.ord ? c.ord(vs) : vs, xs, charsFirst: !!xs };
    }
    if (st.tab === 'colls') return { cs: colls(), vs: [], onlyColls: true };
    if (st.tab === 'new') return { vs: V.filter(x => x.isNew) };
    if (o.lists && o.lists[st.tab]) return { vs: o.lists[st.tab](V), xt: st.tab };
    return recoQuery(o.reco ? o.reco(V) : V.filter(x => x.top));
  }
  function sorted(vs: VoiceItem[]) {
    if (st.sort === 'name') return [...vs].sort((p, n) => p.name.localeCompare(n.name, lang()));
    if (st.sort === 'name_desc') return [...vs].sort((p, n) => n.name.localeCompare(p.name, lang()));
    if (st.sort === 'new') return [...vs].sort((p, n) => bit(n.isNew) - bit(p.isNew));
    return vs;
  }
  const row = (x: VoiceItem) => {
    const on = same(st.value, x.id);
    const pl = st.playing != null && same(st.playing, x.id);
    const loading = previewLoading !== null && same(previewLoading, x.id);
    const tg = o.tag ? o.tag(x) : '';
    return `<div class="v${on ? ' on' : ''}" data-voice="${esc(x.id)}" role="radio" aria-checked="${on}" tabindex="0">
  <button class="orb${pl ? ' on' : ''}" type="button" data-play="${esc(x.id)}" aria-busy="${loading}" style="background:${x.orb}" aria-label="${esc(t('Прослушать {n}', { n: x.name }))}"${(isServerMode() || window.BotServerMe) && !x.mine && !x.preview_url?.startsWith('https://') ? ' disabled' : ''}><span class="pl">${loading ? '<span class="spin"></span>' : pl ? I.pause : I.play}</span></button>
  <div class="m"><b>${esc(x.name)}${tg ? (typeof tg === 'string' ? `<span class="tag">${esc(tg)}</span>` : `<span class="tag ${esc(tg.c)}">${esc(tg.t)}${tg.b ? `<sup class="vbadge">${esc(tg.b)}</sup>` : ''}</span>`) : ''}</b>${pl ? '<span class="vpl" aria-hidden="true">&#8203;<span class="prog"><b id="prog"></b></span></span>' : `<span>${esc((x.tags || []).join(' · '))}</span>`}</div>
  <span class="chk">${on ? I.tickS : ''}</span></div>`;
  };
  const collRow = (c: CollItem) => {
    const vs = voices().filter(v => inColl(v, c));
    const faces = c.faces ?? vs.slice(0, 4).map(v => v.orb ?? '');
    const glow = c.cg ?? (c.img ? `url('${c.img}')` : c.ic);
    return `<button class="ccd" type="button" data-vcoll="${esc(c.id)}"${glow ? ` style="--cg:${esc(glow)}"` : ''}><span class="m"><b>${esc(cname(c))}</b><span class="d">${esc(cdesc(c))}</span><span class="k"><span class="fs">${faces
      .map(f => `<span class="face" style="background:${esc(f)}"></span>`)
      .join('')}</span><span>${c.charCount ? `<span class="kl">${t('Персонажей: {n}', { n: c.charCount })}</span>` : ''}<span${c.charCount ? ' class="kl"' : ''}>${t('Голосов: {n}', { n: c.count ?? (isServerMode() ? '—' : vs.length) })}</span></span></span></span><span class="cv">${cimg(c)}</span></button>`;
  };
  const line = (r: Query) => (r.xt && o.rows && o.rows[r.xt]) || row;

  /** Начало текущей загрузки: от него — порог появления скелетона (120 мс, SK_GATE шаблона) */
  let skT0: number | null = null;
  const skDelay = () => 120 - (performance.now() - (skT0 ?? performance.now()));
  /** Минимум показа скелетона (SK_MIN шаблона, 280 мс), если он уже проявился: ответ, пришедший сразу после порога, не сменяет его мгновенно, как вспышку */
  const skHold = () => {
    if (skT0 === null) return 0;
    const shown = performance.now() - skT0 - 120;
    return shown > 0 ? Math.max(0, 280 - shown) : 0;
  };
  const wait = (ms: number) => (ms > 0 ? new Promise<void>(r => setTimeout(r, ms)) : undefined);
  /** На предыдущей отрисовке стоял скелетон: строки следующей входят по очереди (BotCore.results), а не подменяют его резко */
  let skShown = false;
  /** Вкладка, подборка, сортировка и запрос прошлой отрисовки и были ли в ней строки: смена при строках на экране — перестройка, как фильтры «Студии» */
  let shownKey = '';
  let hadRows = false;
  /** Слой копий ушедших строк для BotCore.results: рядом с колонкой (её разметка перестраивается целиком), с классами колонки и ленты — копии
      сохраняют вид; обрезан видимой частью списка без нижнего поля под меню (voice-skeleton.css, .vlg) */
  function ghosts(): HTMLElement | null {
    const host = el.parentElement;
    const sc = el.querySelector<HTMLElement>('#vscroll');
    if (!host || !sc) return null;
    let g = host.querySelector<HTMLElement>(':scope > .vlg');
    if (!g) {
      g = document.createElement('div');
      g.className = 'cbody vl ccards vlg';
      g.setAttribute('aria-hidden', 'true');
      host.appendChild(g);
    }
    const r = sc.getBoundingClientRect();
    const b = g.getBoundingClientRect();
    /* по краям колонки, нижнее поле не вычитается: на телефоне оно под прозрачным меню, и строки там видны — копии, обрезанные по полю,
       исчезали под меню сразу (пустая тёмная полоса и срезанная аватарка), пока новые строки ещё не въехали */
    g.style.clipPath = `inset(${r.top - b.top}px ${b.right - r.right}px ${b.bottom - r.bottom}px ${r.left - b.left}px)`;
    return g;
  }
  const results = (window as unknown as { BotCore?: { results?: (o: object, paint: () => void) => boolean } }).BotCore?.results;
  function render() {
    if (o.remote && keyOf() !== reqKey) return scheduleRemote();
    const q = st.q.trim();
    if (remoteLoading || collectionsLoading) skT0 ??= performance.now(); else skT0 = null;
    const r = query();
    const c = st.coll && !q ? collOf(st.coll) : null;
    const vs = remoteActive() ? r.vs : sorted(r.vs);
    const shown = remoteActive() ? vs : vs.slice(0, st.shown);
    const more = remoteActive() ? Math.max(0, remoteTotal - shown.length) : vs.length - shown.length;
    const total = num(remoteActive() ? remoteCatalogTotal : o.total || voices().length);
    /* сколько голосов в выдаче: у сервера — всего, а не сколько страниц уже подгружено (подгрузка дописывает строки без перерисовки) */
    const found = remoteActive() ? Math.max(remoteTotal, vs.length) : vs.length;
    /* поиск и «Рекомендуем» — группы с заголовками; у «Рекомендуем» справа «Все» (вкладка группы), у поиска — счётчик */
    const grouped = !!q || !!r.reco || !!r.charsFirst;
    const go = (tab: string) => (r.reco && TABS.some(([id]) => id === tab) ? tab : undefined);
    const head = (title: string, count: string | number, tab?: string) => {
      const to = tab && go(tab);
      return `<div class="vgroup"><span>${title}</span>${to ? `<button class="vall" type="button" data-vgo="${to}">${esc(t('Все'))}</button>` : `<span>${count}</span>`}</div>`;
    };
    /* подборка с персонажами и голосами — вторая строка вкладок «Все · Персонажи · Голоса» (как вторая строка форматов «Студии», .cseg2 .seg.sub):
       сразу к нужной группе, без долгой прокрутки; смена — перестройкой строк, как смена вкладки */
    const parts = !q && !!st.coll && !!r.charsFirst && !!r.xs && r.xs.items.length > 0 && (found > 0 || remoteLoading);
    /* «Все» нет: переключатель показывает одну группу, по умолчанию персонажей */
    const part = parts ? (st.part === 'voices' ? 'voices' : 'chars') : 'all';
    const xsHtml = r.xs && r.xs.items.length && part !== 'voices'
      ? `${parts ? '' : head(esc(r.xs.title), r.xs.items.length, 'chars')}<div class="vl vlx">${r.xs.items.map(r.xs.row).join('')}</div>` : '';
    /* разметка — компонент шаблона как есть: строка контекста subRowHTML (assets/bot-studio.js) — .seg.sub.st-sg, подпись .l с data-t
       (невидимая полужирная копия держит ширину, выбранный пункт не толкает соседей) */
    const partsHtml = parts
      ? `<div class="seg anim sub nosb st-sg vpart" role="tablist" aria-label="${esc(t('Состав подборки'))}">${/* без счётчиков: числа — на плашке подборки («Персонажей: 8 · Голосов: 18»), в строке на месте вкладок они теснили подписи под подсветку */
      ([['chars', t('Персонажи и актёры')], ['voices', t('Голоса')]] as const)
        .map(([id, n]) => `<button type="button" role="tab" data-vpart="${id}" class="${part === id ? 'on' : ''}" aria-selected="${part === id}"><span class="l" data-t="${esc(n)}">${esc(n)}</span></button>`).join('')}</div>`
      : '';
    /* в подборке с персонажами строка «Персонажи · Голоса» встаёт на место вкладок (владелец 30.10.2026): к вкладкам — выход из подборки;
       вид — как у вкладок (без .sub — мелкой стеклянной второй строки) */
    const partsTop = partsHtml.replace('seg anim sub nosb st-sg vpart', 'seg anim nosb vpart vtop');
    const seg = el.querySelector('.vseg');
    const psl = seg ? seg.scrollLeft : 0;
    /* таблетка вкладок переносится в новую разметку с того места, где она видна сейчас, а не с цели её перехода:
       у сервера нажатие даёт две-три перерисовки подряд (нажатие, начало загрузки, ответ), и с целью таблетка прыгала в конец, не доехав */
    const pseg = el.querySelector<HTMLElement>('.vseg .ind');
    const pcs = pseg ? getComputedStyle(pseg) : null;
    const ptr = pcs && pcs.transform !== 'none' ? pcs.transform : pseg ? pseg.style.transform : null;
    const pw = pcs && parseFloat(pcs.width) > 0 ? pcs.width : pseg ? pseg.style.width : null;
    /* таблетка второй строки — так же: с того места, где она видна сейчас */
    const ppart = el.querySelector<HTMLElement>('.vpart .ind');
    const ppcs = ppart ? getComputedStyle(ppart) : null;
    const ptr2 = ppcs && ppcs.transform !== 'none' ? ppcs.transform : null;
    const pw2 = ppcs && parseFloat(ppcs.width) > 0 ? ppcs.width : null;
    const hadParts = !!el.querySelector('.vpart');
    el.classList.add('fixed');
    /* шапка (владелец 30.10.2026, как «Все инструменты» «Студии»): вкладки, лупа и ⇅ — одной строкой; поле поиска лежит в этой же строке
       поверх вкладок и раскрывается по лупе (.vhead.q, legacy/shell.css «Поиск голосов лупой»), с запросом — раскрыто всегда */
    const qo = !!st.q || !!st.qo;
    const html = `<div class="vhead vlh${qo ? ' q' : ''}">
      <div class="vrow"><div class="pane st-cs vcs"><div class="cseg2 one">${parts ? partsTop : `<div class="seg vseg">${TABS.map(([id, n]) => `<button type="button" class="${(st.tab === id && !st.coll) || (id === 'colls' && st.coll) ? 'on' : ''}" data-vtab="${id}"${qo ? ' tabindex="-1"' : ''}>${t(n)}</button>`).join('')}</div>`}</div></div><button class="sorticon vlens" type="button" data-vlens aria-label="${esc(t('Поиск'))}"${qo ? ' tabindex="-1"' : ''}>${I.search}</button><button class="sorticon${st.sort !== 'pop' || st.only === 's2s' ? ' on' : ''}" type="button" id="vsortbtn" aria-label="${esc(t('Сортировка'))}">${I.sort}</button><button class="iconb xbtn" type="button" data-vclose aria-label="${esc(t('Закрыть выбор голоса'))}">${I.x18}</button>
      <div class="search vqf">${I.search}<input id="vq" type="search" placeholder="${esc(t('Искать среди {n}+ голосов', { n: total }))}" value="${esc(st.q)}" autocomplete="off"${qo ? '' : ' tabindex="-1"'}><button class="sx" type="button" data-vqx aria-label="${esc(t('Закрыть поиск'))}"${qo ? '' : ' tabindex="-1"'}>${I.x}</button></div></div>
    </div>
    <div class="vscroll${c ? ' isl' : ''}" id="vscroll">
      ${st.note ? `<div class="note ${st.note.kind === 'ok' ? 'ok' : 'mute'} vnote" role="status">${st.note.kind === 'ok' ? I.check : I.info}<span>${esc(t(st.note.text))}</span></div>` : ''}
      ${previewError ? `<p role="alert">${esc(t(previewError))}</p>` : ''}
      ${r.cs && r.cs.length ? `${grouped ? head(t('Подборки'), r.cs.length, 'colls') : ''}<div class="vl ccards${grouped ? ' sr' : ''}">${r.cs.map(collRow).join('')}</div>` : r.onlyColls && collectionsLoading ? skCards(4, skDelay()) : r.reco && collectionsLoading ? skCards(RECO_COLLS, skDelay()) : ''}
      ${r.reco || r.charsFirst ? xsHtml : ''}
      ${
        r.onlyColls || part === 'chars' || (grouped && !vs.length && ((r.cs && r.cs.length) || (r.xs && r.xs.items.length)))
          ? ''
          : `${grouped && !parts && (shown.length || (remoteActive() && remoteLoading)) ? head(r.title ?? '', r.reco ? shown.length : r.charsFirst ? found : t('{a} из {b}', { a: found, b: total })) : ''}
      <div class="vl" id="vl">${shown.length ? shown.map(line(r)).join('') : remoteActive() && remoteLoading ? skRows(9, skDelay()) : remoteActive() && remoteError ? `<div class="vgroup" style="justify-content:center;padding:20px 0">${t('Не удалось загрузить голоса')}</div>` : (r.xt && o.empty && o.empty[r.xt]) || zero(q)}</div>
      ${r.reco ? '' : (remoteActive() ? shown.length && (remoteCursor || remoteLoading) : more > 0) ? skRows(Math.min(3, Math.max(1, more)), skDelay(), ' id="vmore"') : ''}`
      }
      ${r.reco || r.charsFirst ? '' : xsHtml}
    </div>
    <div class="cislb">${c ? `<div class="cisl"><span class="icv">${cimg(c)}</span><span class="m"><b>${esc(cname(c))}</b><small>${t('Подборка')} · ${c.charCount ? `${t('Персонажей: {n}', { n: c.charCount })} · ` : ''}${t('Голосов: {n}', { n: found })}</small></span><button class="iconb" type="button" data-vcoll="" data-vcoll-back aria-label="${esc(t('Закрыть подборку'))}">${I.x}</button></div>` : ''}</div>`;
    /* выдача сервера приходит позже ввода: поле, в котором набирают, после перерисовки снова в фокусе и с тем же курсором
       (иначе на телефоне закрывалась клавиатура посреди набора; быстрый поиск каркаса без сервера оставляет поле на месте сам) */
    const typing = document.activeElement instanceof HTMLInputElement && document.activeElement.id === 'vq' && el.contains(document.activeElement)
      ? document.activeElement : null;
    const caret = typing ? typing.selectionStart : null;
    /* скелетон вместо списка — без #vmore (хвост подгрузки под строками не в счёт) */
    const nowSk = (html.match(/<div class="lsk"[^>]*>/g) || []).some(tag => !tag.includes('id="vmore"'));
    const key = [st.tab, st.coll ?? '', st.sort, q, part].join('|');
    const rows = !nowSk && /class="(v|ccd)[" ]/.test(html);
    /* перестройка, как фильтры «Студии» (BotCore.results): после скелетона строки входят по очереди (сдвиг 10 px, 280 мс, через 28 мс);
       смена вкладки, подборки, сортировки или запроса, пока строки на экране, — оставшиеся едут на новое место, новые входят по очереди, ушедшие гаснут копиями;
       с выдачи без строк (пустые «Мои», «Ничего не нашлось») на выдачу со строками — строки тоже входят по очереди, а не проявляются списком целиком */
    const changed = !!shownKey && key !== shownKey;
    /* сменился сам список — вкладка, подборка или «Персонажи · Голоса» (не запрос и не порядок): строки входят по очереди все, а не едут —
       строка, оказавшаяся на той же высоте (общий персонаж двух списков), иначе стояла на месте, пока остальные входили */
    const was = shownKey.split('|');
    const navChanged = changed && (was[0] !== st.tab || was[1] !== (st.coll ?? '') || was[4] !== part);
    const morph = rows && (skShown || changed) || hadRows && changed;
    /* смена на выдачу без строк (пустые «Мои», «Ничего не нашлось») — она проявляется целиком; при строках — только перестройка:
       раньше на каждой смене список ещё и проявлялся целиком (pop #vscroll) поверх входа строк, и всё мигало дважды */
    const swap = !morph && !!shownKey && key !== shownKey;
    /* заглушки сменились выдачей без строк (пустые «Мои», ошибка) — она проявляется, а не возникает на месте скелетона */
    const settle = !morph && skShown && !nowSk && !rows;
    /* выдача без строк встаёт карточкой (пустые «Мои», «Ничего не нашлось»): и с другой вкладки, и из памяти, и после скелетона
       карточка входит так же, как строки других вкладок, — раньше из памяти она возникала разом, пока старые строки гасли */
    const emptyIn = !rows && !nowSk && (morph || swap || settle);
    /* список новый (вкладка, подборка, «Персонажи · Голоса») или встаёт на место скелетона — входят все видимые строки */
    const freshList = navChanged || skShown;
    shownKey = key;
    hadRows = rows;
    skShown = nowSk;
    const paint = () => commit(html, () => {
      const field = typing && el.querySelector<HTMLInputElement>('#vq');
      if (field && document.activeElement !== field) {
        field.focus();
        if (caret != null) try { field.setSelectionRange(caret, caret); } catch { /* поле без курсора */ }
      }
      const vsg = el.querySelector<HTMLElement>('.vseg');
      if (vsg) {
        if (ptr) {
          const ind = document.createElement('span');
          ind.className = 'ind';
          ind.style.transition = 'none';
          ind.style.transform = ptr;
          if (pw) ind.style.width = pw;
          vsg.prepend(ind);
          void vsg.offsetWidth;
          ind.style.transition = '';
        }
        segInd(vsg);
        vsg.scrollLeft = psl;
        /* края сегмента (BotCore.fade, --ml/--mr): шаблон ставит их после постановки списка (voiceHost.mount — на следующем кадре, вкладка — syncSeg);
           страница каталога сервера приходит позже и ставит сегмент заново — без этого правый край «Персонажей» не растворялся */
        (window as unknown as { BotCore?: { fade?: (el: Element) => void } }).BotCore?.fade?.(vsg);
      }
      const vpart = el.querySelector<HTMLElement>('.vpart');
      if (vpart) {
        if (ptr2) {
          const ind = document.createElement('span');
          ind.className = 'ind';
          ind.style.transition = 'none';
          ind.style.transform = ptr2;
          if (pw2) ind.style.width = pw2;
          vpart.prepend(ind);
          void vpart.offsetWidth;
          ind.style.transition = '';
        }
        segInd(vpart);
      }
      /* вошли в подборку или вышли из неё — строка сменилась (вкладки ⇄ «Персонажи · Голоса»): новая встаёт по очереди слева направо, как строки списка (.vin) */
      if (hadParts !== !!vpart && !reduced()) seqIn();
      syncGutters('.cbody,.vscroll');
      const sc = el.querySelector<HTMLElement>('#vscroll');
      if (!sc) return;
      sc.scrollTop = st.top;
      sc.addEventListener('scroll', onScroll, { passive: true });
      const zero = emptyIn ? sc.querySelector<HTMLElement>('.vmz') : null;
      if (zero) {
        if (!reduced())
          zero.animate([{ opacity: 0, transform: 'translateY(10px) scale(.97)' }, { opacity: 1, transform: 'none' }],
            { duration: 280, delay: 70, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' });
      } else if (swap || settle) pop(sc);
    });
    const layer = morph && results ? ghosts() : null;
    /* у строки должен быть ключ: строку без ключа BotCore.results пропускает — она не входит, не едет и не гаснет копией,
       поэтому персонажи и актёры (.v.crow, data-char) ключуются наравне с голосами и подборками */
    if (layer && results) {
      results({ root: el, sel: '.vl > .v, .ccards > .ccd', ghosts: layer, far: navChanged ? -1 : 600,
      key: (row: HTMLElement) => (row.dataset.voice ? 'v:' + row.dataset.voice : row.dataset.vcoll ? 'c:' + row.dataset.vcoll
        : row.dataset.char ? 'h:' + row.dataset.char : '') }, paint);
      /* перестройка меряет строки сразу после вставки, и нижние видимые (под меню) порой выходили за край окна и не входили — стояли на месте,
         пока верхние въезжали: им — тот же вход в продолжение очереди */
      if (freshList && !reduced()) {
        const all = [...el.querySelectorAll<HTMLElement>('.vl > .v, .ccards > .ccd')];
        const moving = all.filter(row => row.getAnimations().length).length;
        /* без замера места: сразу после вставки строки ещё выше своей высоты (78 px вместо 62), и нижние видимые казались за краем окна */
        enterRows(all.slice(0, 20).filter(row => !row.getAnimations().length), moving, true);
      }
    } else if (rows) {
      /* тот же список дорисовался (страница сервера пришла после первой порции из каталога): новые строки въезжают тем же входом, а не возникают —
         иначе нижние строки стояли на месте, пока верхние входили */
      const keyOf = (row: HTMLElement) => row.dataset.voice ? 'v:' + row.dataset.voice : row.dataset.vcoll ? 'c:' + row.dataset.vcoll : row.dataset.char ? 'h:' + row.dataset.char : '';
      const before = new Set([...el.querySelectorAll<HTMLElement>('.vl > .v, .ccards > .ccd')].map(keyOf));
      paint();
      enterRows([...el.querySelectorAll<HTMLElement>('.vl > .v, .ccards > .ccd')].filter(row => !before.has(keyOf(row))));
    } else paint();
  }
  function onScroll(e: Event) {
    const sc = e.target;
    if (!(sc instanceof HTMLElement)) return;
    st.top = sc.scrollTop;
    const vm = el.querySelector('#vmore');
    if (!vm || vm.getBoundingClientRect().top > sc.getBoundingClientRect().bottom + 80) return;
    if (remoteActive()) {
      if (remoteCursor && !remoteLoading) void loadRemote(remoteRevision, true);
      return;
    }
    const qr = query();
    const vs = sorted(qr.vs);
    const rw = line(qr);
    const next = vs.slice(st.shown, st.shown + 10);
    if (!next.length) return;
    st.shown += next.length;
    const list = el.querySelector('#vl');
    if (list) {
      const from = list.children.length;
      list.insertAdjacentHTML('beforeend', next.map(rw).join(''));
      enterRows([...list.children].slice(from) as HTMLElement[]);
    }
    if (vs.length - st.shown <= 0) vm.remove();
  }
  function scheduleRemote(delay = 0) {
    if (remoteTimer) clearTimeout(remoteTimer);
    remoteRevision++;
    reqKey = keyOf();
    /* «Избранное» меняется нажатием сердца и собирается на месте — из памяти выдач не берётся */
    const hit = st.q.trim() || st.coll === 'fav' ? undefined : cache.get(reqKey);
    const kept = hit && performance.now() - hit.at < CACHE_MS ? hit : null;
    remoteItems = kept ? kept.items : [];
    remoteCursor = kept ? kept.cursor : null;
    remoteTotal = kept ? kept.total : 0;
    remoteReco = kept ? kept.reco : null;
    if (kept) remoteCatalogTotal = kept.catalogTotal;
    remoteError = false;
    remoteLoading = remoteActive() && !kept;
    if (st.q.trim()) searchCollections();
    else if (isReco() && kept && !kept.reco) recoCollections();
    render();
    if (!remoteActive()) {
      if (st.tab === 'colls' && o.remoteCollections && !remoteColls.length) void loadCollections(remoteRevision);
      return;
    }
    /* подборка открыта из памяти (перезагрузка, возврат в раздел), а её карточки нет ни в одном списке — догрузить подборки:
       без карточки не было плашки с названием и «Закрыть подборку» (и «Назад» Telegram) */
    if (st.coll && !collOf(st.coll) && o.remoteCollections && !remoteColls.length && !collectionsLoading) void loadCollections(remoteRevision);
    if (kept) return;
    const revision = remoteRevision;
    remoteTimer = setTimeout(() => { void loadRemote(revision, false); }, delay);
  }
  /** Подборки «Рекомендуем»: только первые, короткой страницей; весь набор подгрузит вкладка «Подборки» или прогрев каркаса */
  function recoCollections() {
    if (!o.remoteCollectionsTop) return searchCollections();
    if (!o.remoteCollections || remoteColls.length || recoHead.length || collectionsLoading) return;
    collectionsLoading = true;
    o.remoteCollectionsTop(RECO_COLLS)
      .then(items => { recoHead = items; HEADS.set(o.remoteCollectionsTop!, items); }, () => undefined)
      .finally(() => { collectionsLoading = false; if (isReco()) render(); });
  }
  /** Подборки для группы «Подборки» поиска: один запрос на весь набор запроса, не привязан к ревизии выдачи (буквы идут быстрее ответа) */
  function searchCollections() {
    if (!o.remoteCollections || remoteColls.length || collectionsLoading) return;
    collectionsLoading = true;
    o.remoteCollections()
      .then(items => { keepColls(items); }, () => undefined)
      .finally(() => { collectionsLoading = false; if (st.q.trim() || isReco()) render(); });
  }
  async function loadCollections(revision: number) {
    if (!o.remoteCollections) return;
    collectionsLoading = true;
    render();
    try {
      const items = await o.remoteCollections();
      await wait(skHold());
      if (revision !== remoteRevision) return;
      keepColls(items);
    } catch {
      if (revision !== remoteRevision) return;
      remoteColls = [];
    } finally {
      if (revision === remoteRevision) { collectionsLoading = false; render(); }
    }
  }
  async function loadRemote(revision: number, append: boolean) {
    if (!o.remote || revision !== remoteRevision || append && (!remoteCursor || remoteLoading)) return;
    remoteLoading = true;
    /* следующая страница: скелетон #vmore уже стоит под списком, перерисовка не нужна */
    if (!append) render();
    let inPlace = false;
    try {
      const page = await o.remote({ tab: st.tab, q: st.q, sort: st.sort, coll: st.coll, only: st.only }, append ? remoteCursor! : undefined);
      if (!append) await wait(skHold());
      if (revision !== remoteRevision) return;
      const added = append ? page.items.filter(item => !remoteItems.some(old => same(old.id, item.id))) : page.items;
      remoteItems = append ? [...remoteItems, ...added] : added;
      remoteCursor = page.nextCursor;
      remoteTotal = page.total;
      remoteCatalogTotal = page.catalogTotal;
      remoteError = false;
      if (!append && isReco()) {
        remoteReco = page.collections || page.characterIds ? { colls: page.collections || [], ids: page.characterIds || [] } : null;
        /* сервер прислал страницу без подборок (старая версия) — подборки грузятся отдельным коротким запросом */
        if (!page.collections?.length) recoCollections();
      }
      if (!st.q.trim() && st.coll !== 'fav') cache.set(reqKey, { at: performance.now(), items: remoteItems, cursor: remoteCursor, total: remoteTotal, catalogTotal: remoteCatalogTotal, reco: remoteReco });
      inPlace = append && appendRows(added);
    } catch {
      if (revision !== remoteRevision) return;
      remoteError = true;
      remoteCursor = null;
    } finally {
      if (revision === remoteRevision) {
        remoteLoading = false;
        if (!inPlace) render();
      }
    }
  }
  /** Дописанные строки (следующая страница) входят так же, как первая порция (BotCore.results): сдвиг 10 px и проявление за 280 мс, по очереди через 28 мс;
      только те, что видны на экране (и под меню) — остальные встанут на место молча, к ним ещё надо долистать */
  function enterRows(rows: HTMLElement[], start = 0, anyPlace = false) {
    if (reduced()) return;
    const vh = innerHeight;
    let n = start;
    /* сначала обмер всех строк, потом анимации: запуск анимации между обмерами заставлял браузер пересчитывать стили на каждую строку */
    const seen = rows.map(r => {
      if (anyPlace) return true;
      const b = r.getBoundingClientRect();
      return !(b.bottom < 0 || b.top > vh);
    });
    rows.forEach((r, i) => {
      if (!seen[i]) return;
      r.animate?.([{ opacity: 0, transform: 'translateY(10px) scale(.97)' }, { opacity: 1, transform: 'none' }], { duration: 280, delay: 70 + Math.min(n++, 8) * 28, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' });
    });
  }
  /** Следующая страница — строками в конец списка: прокручиваемый блок не пересоздаётся, и инерция прокрутки на телефоне не обрывается */
  function appendRows(items: VoiceItem[]) {
    const list = el.querySelector('#vl');
    const more = el.querySelector('#vmore');
    if (!list || !more || previewError) return false;
    const from = list.children.length;
    list.insertAdjacentHTML('beforeend', items.map(line(query())).join(''));
    if (!remoteCursor) more.remove();
    enterRows([...list.children].slice(from) as HTMLElement[]);
    return true;
  }
  /** Прослушивание меняет только свои строки (кнопка, пауза, полоса): перерисовка всего списка пересоздавала все строки и аватарки (мигание)
      и заново ставила шапку с таблеткой вкладок. Строки своего вида (персонажи) и сообщение об ошибке — целой перерисовкой */
  function paintRows(...ids: unknown[]) {
    const draw = line(query());
    const pool = remoteActive() ? remoteItems : voices();
    for (const id of new Set(ids.filter(x => x != null).map(String))) {
      const cur = [...el.querySelectorAll<HTMLElement>('#vl > .v[data-voice]')].find(r => r.dataset.voice === id);
      if (!cur) continue;
      const item = pool.find(v => same(v.id, id));
      if (!item || draw !== row) return render();
      const wasBar = !!cur.querySelector('.m > .vpl');
      cur.insertAdjacentHTML('afterend', row(item));
      const next = cur.nextElementSibling;
      cur.remove();
      /* прослушивание кончилось: теги возвращаются на место полосы проявлением, а не возникают (полоса входит своей анимацией, voice-row.css) */
      const tags = wasBar && next?.querySelector<HTMLElement>('.m > span:not(.vpl)');
      if (tags && tags.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches && !document.documentElement.classList.contains('still'))
        tags.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
    }
  }
  function play(id: string) {
    if (isServerMode() || window.BotServerMe) {
      const was = st.playing != null && same(st.playing, id) || previewLoading !== null && same(previewLoading, id);
      const prev = [st.playing, previewLoading];
      const hadError = !!previewError;
      stop(); previewError = '';
      const repaint = (...ids: unknown[]) => (hadError || previewError ? render() : paintRows(...ids));
      if (was) return repaint(...prev);
      const voice = (remoteActive() ? remoteItems : voices()).find(voice => same(voice.id, id));
      if (!voice) return;
      const request = new AbortController(); previewRequest = request; previewLoading = id; repaint(...prev, id);
      void loadVoicePreview(voice, request.signal).then(source => {
        if (request.signal.aborted || previewRequest !== request) { source.release(); return; }
        releasePreview = source.release; previewLoading = null;
        const audio = new Audio(source.url); preview = audio; st.playing = id; paintRows(id);
        const finish = () => { if (preview !== audio) return; stop(); if (previewError) render(); else paintRows(id); };
        const failed = () => { if (preview !== audio) return; previewError = 'Не удалось воспроизвести пример. Попробуйте ещё раз.'; finish(); };
        audio.onended = finish; audio.onerror = failed;
        audio.ontimeupdate = () => {
          if (preview !== audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
          const bar = el.querySelector<HTMLElement>('#prog'); if (bar) bar.style.width = (100 * audio.currentTime) / audio.duration + '%';
        };
        void audio.play().catch(failed);
      }).catch(() => {
        if (request.signal.aborted || previewRequest !== request) return;
        stop(); previewError = 'Не удалось воспроизвести пример. Попробуйте ещё раз.'; render();
      });
      return;
    }
    if (pt) clearInterval(pt);
    const was = st.playing != null && same(st.playing, id);
    const prev = st.playing;
    st.playing = was ? null : id;
    paintRows(prev, id);
    if (was) return;
    let p = 0;
    pt = setInterval(() => {
      p += 2;
      const b = el.querySelector<HTMLElement>('#prog');
      if (b) b.style.width = p + '%';
      if (p >= 100) {
        if (pt) clearInterval(pt);
        st.playing = null;
        paintRows(id);
      }
    }, 60);
  }
  const stop = () => {
    previewRequest?.abort(); previewRequest = null; previewLoading = null;
    if (pt) clearInterval(pt);
    if (preview) {
      preview.onended = null;
      preview.onerror = null;
      preview.ontimeupdate = null;
      preview.pause();
      preview.removeAttribute('src');
      preview.load();
      preview = null;
    }
    releasePreview?.(); releasePreview = null;
    st.playing = null;
  };
  /** Открыть подборку списком (null — закрыть): нажатие на карточку подборки и подборка из карточки голоса сервера */
  function openColl(id: string | null) {
    openedColl = (id && collOf(id)) || null;
    st.coll = id;
    st.part = 'all';
    st.q = '';
    st.tab = 'colls';
    reset();
    if (o.remote) scheduleRemote(); else render();
    pop(el.querySelector('.cisl'));
  }
  function onClick(e: Event) {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const x = target.closest<HTMLElement>(
      '[data-play],[data-voice],[data-vtab],[data-vgo],[data-vpart],[data-vcoll],[data-vsort],[data-vclose],[data-vclear],[data-vlens],[data-vqx],#vsortbtn',
    );
    const menu = el.querySelector('#vsortmenu');
    e.stopPropagation();
    if (!x) {
      menu?.classList.remove('open');
      return;
    }
    if (x.dataset.play !== undefined) {
      play(x.dataset.play);
      return;
    }
    if (x.dataset.vlens !== undefined) {
      searchOpen(true);
      return;
    }
    if (x.dataset.vqx !== undefined) {
      searchOpen(false);
      return;
    }
    if (x.dataset.vclear !== undefined) {
      /* «Очистить поиск» пустой выдачи — и поле сворачивается к лупе, вкладки возвращаются */
      st.q = '';
      st.qo = false;
      reset();
      if (o.remote) scheduleRemote(); else render();
      return;
    }
    if (x.dataset.vpart) {
      st.part = x.dataset.vpart as VoiceListState['part'];
      st.top = 0;
      render();
      return;
    }
    if (x.dataset.vtab || x.dataset.vgo) {
      st.tab = x.dataset.vtab || x.dataset.vgo!;
      st.coll = null;
      st.part = 'all';
      reset();
      if (o.remote) scheduleRemote(); else render();
      return;
    }
    if (x.dataset.vcoll !== undefined) {
      openColl(x.dataset.vcoll || null);
      return;
    }
    /* ⇅ — лист «Сортировка и фильтры» плитками (ui/filter-sheet.ts, владелец 30.10.2026): порядок и отбор «Голоса» — все или только с переозвучкой записи
       (у списка, уже отобранного по переозвучке, отбора нет) */
    if (x.id === 'vsortbtn') {
      openFilterSheet({
        groups: () => [
          { key: 'sort', label: t('Сначала'), items: SORTS.map(([id, n]) => ({ id, label: t(n), sub: t(FILTER_SUB[id] ?? '') })), value: [st.sort], def: ['pop'] },
          ...(o.onlyFixed ? [] : [{
            key: 'only', label: t('Голоса'), value: [st.only === 's2s' ? 's2s' : 'all'], def: ['all'],
            items: [
              { id: 'all', label: t('Все голоса'), sub: t('Озвучка текста и переозвучка') },
              { id: 's2s', label: t('Переозвучка записи'), sub: t('Меняют голос в вашей записи') },
            ],
          }]),
        ],
        t: { title: t('Сортировка и фильтры'), close: t('Закрыть'), reset: t('Сбросить'), done: t('Готово') },
        onChange: (key, value) => {
          if (key === 'only') {
            const only = value[0] === 's2s' ? 's2s' : '';
            if (only === (st.only || '')) return;
            st.only = only;
            /* отбор сняли — вход за голосом для переозвучки окончен: плашка уходит, выбор голоса снова обычный */
            if (!only) st.note = null;
            reset();
            if (o.remote) scheduleRemote(); else render();
            return;
          }
          if (!value[0] || value[0] === st.sort) return;
          st.sort = value[0];
          reset();
          if (o.remote) scheduleRemote(); else render();
        },
      });
      return;
    }
    menu?.classList.remove('open');
    if (x.dataset.vclose !== undefined) {
      stop();
      o.onClose?.();
      return;
    }
    if (x.dataset.voice !== undefined) {
      const v = (remoteActive() ? remoteItems : voices()).find(item => same(item.id, x.dataset.voice));
      if (!v) return;
      stop();
      st.value = v.id;
      o.onPick?.(v.id, v);
    }
  }
  /** Поле поиска поверх вкладок: раскрыть по лупе (фокус в поле) или свернуть (крестик, Escape, уход из пустого поля). Класс меняется на стоящей шапке —
      переход успевает сыграть; запрос при сворачивании сбрасывается после него (перерисовка вернёт выдачу вкладки) */
  let qTimer: ReturnType<typeof setTimeout> | null = null;
  /** Строка вкладок (или «Персонажи · Голоса» подборки) входит по очереди слева направо (.vin, legacy/shell.css): номер кнопки — задержка */
  function seqIn() {
    const head = el.querySelector<HTMLElement>('.vlh');
    if (!head) return;
    head.querySelectorAll<HTMLElement>('.vcs .seg>button').forEach((b, i) => b.style.setProperty('--i', String(i)));
    head.classList.remove('vin');
    void head.offsetWidth;
    head.classList.add('vin');
    setTimeout(() => head.classList.remove('vin'), 720);
  }
  function searchOpen(on: boolean) {
    const head = el.querySelector<HTMLElement>('.vlh');
    if (qTimer) clearTimeout(qTimer);
    qTimer = null;
    st.qo = on;
    if (!head) return;
    head.classList.toggle('q', on);
    head.querySelectorAll<HTMLElement>('.vcs .seg button, [data-vlens]').forEach(b => (on ? b.setAttribute('tabindex', '-1') : b.removeAttribute('tabindex')));
    head.querySelectorAll<HTMLElement>('#vq, [data-vqx]').forEach(b => (on ? b.removeAttribute('tabindex') : b.setAttribute('tabindex', '-1')));
    const field = head.querySelector<HTMLInputElement>('#vq');
    if (on) {
      field?.focus({ preventScroll: true });
      return;
    }
    field?.blur();
    /* вкладки возвращаются по очереди слева направо, как входят строки списка */
    if (!reduced()) seqIn();
    if (!st.q) return;
    qTimer = setTimeout(() => {
      qTimer = null;
      st.q = '';
      reset();
      if (o.remote) scheduleRemote(); else render();
    }, reduced() ? 0 : 700); /* после того как поле стянулось и вкладки встали (legacy/shell.css): перерисовка не обрывает переход */
  }
  /* свайп по списку влево и вправо (владелец 30.10.2026) — соседняя кнопка строки шапки: вкладка, а в подборке «Персонажи и актёры ⇄ Голоса».
     Нажатие той же кнопки — переход тот же, что касанием. Жест — сдвиг больше 60 px и вдвое больше вертикального; при открытом поиске — нет */
  let swipe: { x: number; y: number } | null = null;
  function onTouchStart(e: TouchEvent) {
    const p = e.touches[0];
    swipe = e.touches.length === 1 && p && e.target instanceof Element && e.target.closest('#vscroll') && !st.qo && !st.q ? { x: p.clientX, y: p.clientY } : null;
  }
  function onTouchEnd(e: TouchEvent) {
    const s0 = swipe;
    swipe = null;
    const p = e.changedTouches[0];
    if (!s0 || !p) return;
    const dx = p.clientX - s0.x,
      dy = p.clientY - s0.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 2) return;
    const bs = [...el.querySelectorAll<HTMLButtonElement>('.vlh .vcs .seg>button')];
    const at = bs.findIndex(b => b.classList.contains('on'));
    const to = bs[at + (dx < 0 ? 1 : -1)];
    if (at < 0 || !to) return;
    to.click();
    to.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
  }
  /** Ушли из пустого поля (не на его крестик) — поле сворачивается к лупе */
  function onFocusOut(e: FocusEvent) {
    const target = e.target;
    if (!(target instanceof HTMLInputElement) || target.id !== 'vq' || st.q) return;
    const to = e.relatedTarget;
    if (to instanceof Element && to.closest('.vqf')) return;
    if (st.qo) searchOpen(false);
  }
  function onInput(e: Event) {
    const target = e.target;
    if (!(target instanceof HTMLInputElement) || target.id !== 'vq') return;
    e.stopPropagation();
    st.q = target.value;
    reset();
    const p = target.selectionStart;
    if (o.remote) scheduleRemote(250); else render();
    const q = el.querySelector<HTMLInputElement>('#vq');
    if (!q) return;
    q.focus();
    try {
      q.setSelectionRange(p, p);
    } catch {
      /* поле поиска уже снято */
    }
  }
  function onKey(e: KeyboardEvent) {
    const target = e.target;
    const v = target instanceof Element ? target.closest('.v') : null;
    if (v && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      if (v instanceof HTMLElement) v.click();
      return;
    }
    if (e.key === 'Escape') {
      e.stopPropagation();
      /* Escape в раскрытом поиске сворачивает поле, а не закрывает выбор голоса */
      if (st.qo || st.q) {
        searchOpen(false);
        return;
      }
      stop();
      o.onClose?.();
    }
  }
  el.addEventListener('click', onClick);
  el.addEventListener('input', onInput);
  el.addEventListener('keydown', onKey);
  el.addEventListener('focusout', onFocusOut);
  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchend', onTouchEnd, { passive: true });
  /* смена языка профиля: подписи — словарём нового языка, названия голосов и подборок сервер отдаёт на языке запроса (Accept-Language) — запросить заново */
  function onLocale() {
    keepColls([]);
    remoteReco = null;
    recoHead = [];
    if (o.remoteCollectionsTop) HEADS.delete(o.remoteCollectionsTop);
    cache.clear();
    if (o.remote) scheduleRemote(); else render();
  }
  /** Избранное изменилось (сердце в карточке, загрузка с сервера): открытое «Избранное» собирается заново, опустело — закрывается;
      иначе перерисовка — карточка «Избранного» в подборках и на «Рекомендуем» появляется, меняет числа или уходит */
  function onFav() {
    if (st.coll === 'fav' && !st.q.trim()) {
      if (!o.favColl?.()) return openColl(null);
      if (o.remote) return scheduleRemote();
    }
    render();
  }
  document.addEventListener('steos:locale', onLocale);
  document.addEventListener('steos:chars', render);
  document.addEventListener('steos:fav', onFav);
  function destroy() {
    document.removeEventListener('steos:locale', onLocale);
    document.removeEventListener('steos:chars', render);
    document.removeEventListener('steos:fav', onFav);
    remoteRevision++;
    if (remoteTimer) clearTimeout(remoteTimer);
    stop();
    el.removeEventListener('click', onClick);
    el.removeEventListener('input', onInput);
    el.removeEventListener('keydown', onKey);
    el.removeEventListener('focusout', onFocusOut);
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchend', onTouchEnd);
    if (qTimer) clearTimeout(qTimer);
    el.classList.remove('fixed');
  }
  if (o.remote) scheduleRemote(); else render();
  /* refresh — выдача на экране устарела (появился свой голос, ввод поиска): запомненную для неё выдачу не брать */
  const refresh = (delay?: number) => {
    cache.delete(keyOf());
    scheduleRemote(delay);
  };
  /* requery — состояние уже поменяли снаружи (быстрый поиск каркаса, pkFast): выдача по нему тем же путём, что ввод в поле —
     запомненная выдача сразу (сброс поиска возвращает вкладку без скелетона), запрос с задержкой delay, перестройка строк — одна, своя */
  const requery = (delay?: number) => scheduleRemote(delay);
  return { render, refresh, requery, destroy, openColl, state: st };
}
